import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import ProductDetails from "./pages/ProductDetails";
import About from "./pages/About";
import Blog from "./pages/Blog";
import Contact from "./pages/Contact";
import Shop from "./pages/Shop";
import Cart from "./pages/Cart";
import { products } from "./data/products";

import "./App.css";
import Login from "./components/Login";
import UserDashboard from "./components/UserDashboard";
import { auth } from "./firebase";

const getRouteFromHash = () => {
  const hash = window.location.hash.replace("#", "") || "/";
  const [, page, productId] = hash.split("/");

  return {
    page: page || "home",
    productId: productId ? Number(productId) : null,
  };
};

function App() {
  const [route, setRoute] = useState(getRouteFromHash);
  const [cartItems, setCartItems] = useState({});
  const [wishlistItems, setWishlistItems] = useState({});
  const [showLogin, setShowLogin] = useState(false);
  const [loginMode, setLoginMode] = useState("login");
  const [showAccount, setShowAccount] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const openLogin = (mode = "login") => {
    setLoginMode(mode);
    setShowLogin(true);
  };

  const closeLogin = () => {
    setShowLogin(false);
  };

  const handleLoginSuccess = () => {
    closeLogin();
    setShowAccount(true);
    window.location.hash = "#/";
    setRoute(getRouteFromHash());
  };

  const openAccount = () => {
    setShowAccount(true);
  };

  const closeAccount = () => {
    setShowAccount(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    setCartItems({});
    setWishlistItems({});
    setShowAccount(false);
    setShowLogin(false);
    window.location.hash = "#/";
    setRoute(getRouteFromHash());
  };

  const requireLogin = () => {
    if (currentUser) {
      return true;
    }

    openLogin("login");
    return false;
  };

  useEffect(() => {
    const handleRouteChange = () => setRoute(getRouteFromHash());

    window.addEventListener("hashchange", handleRouteChange);
    return () => window.removeEventListener("hashchange", handleRouteChange);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "test") {
      window.scrollTo(0, 0);
    }
  }, [route]);

  const cartCount = useMemo(
    () =>
      Object.values(cartItems).reduce((total, quantity) => total + quantity, 0),
    [cartItems],
  );

  const cartProducts = useMemo(
    () =>
      products
        .filter((product) => cartItems[product.id])
        .map((product) => ({
          ...product,
          quantity: cartItems[product.id],
        })),
    [cartItems],
  );

  const cartTotal = useMemo(
    () =>
      cartProducts.reduce(
        (total, product) => total + product.price * product.quantity,
        0,
      ),
    [cartProducts],
  );

  const wishlistProducts = useMemo(
    () => products.filter((product) => wishlistItems[product.id]),
    [wishlistItems],
  );

  const selectedProduct = useMemo(
    () =>
      products.find((product) => product.id === route.productId) || products[0],
    [route.productId],
  );

  const handleAddToCart = (product) => {
    if (!requireLogin()) {
      return;
    }

    setCartItems((currentItems) => ({
      ...currentItems,
      [product.id]: (currentItems[product.id] || 0) + 1,
    }));
  };

  const handleDecreaseCartItem = (productId) => {
    if (!requireLogin()) {
      return;
    }

    setCartItems((currentItems) => {
      const currentQuantity = currentItems[productId] || 0;

      if (currentQuantity <= 1) {
        const nextItems = { ...currentItems };
        delete nextItems[productId];
        return nextItems;
      }

      return {
        ...currentItems,
        [productId]: currentQuantity - 1,
      };
    });
  };

  const handleRemoveCartItem = (productId) => {
    if (!requireLogin()) {
      return;
    }

    setCartItems((currentItems) => {
      const nextItems = { ...currentItems };
      delete nextItems[productId];
      return nextItems;
    });
  };

  const handleToggleWishlist = (productId) => {
    if (!requireLogin()) {
      return;
    }

    setWishlistItems((currentItems) => {
      const nextItems = { ...currentItems };

      if (nextItems[productId]) {
        delete nextItems[productId];
      } else {
        nextItems[productId] = true;
      }

      return nextItems;
    });
  };

  const handleOpenProduct = (product) => {
    window.location.hash = `#/product/${product.id}`;
  };

  const sharedShopProps = {
    onAddToCart: handleAddToCart,
    onOpenDetails: handleOpenProduct,
    onToggleWishlist: handleToggleWishlist,
    cartItems,
    wishlistItems,
  };

  const renderPage = () => {
    if (route.page === "shop" || route.page === "products") {
      return (
        <Shop
          {...sharedShopProps}
          pageTitle={route.page === "products" ? "Products" : "Shop"}
        />
      );
    }

    if (route.page === "cart") {
      return (
        <Cart
          cartItems={cartItems}
          wishlistItems={wishlistItems}
          cartProducts={cartProducts}
          cartTotal={cartTotal}
          onAddToCart={handleAddToCart}
          onDecreaseCartItem={handleDecreaseCartItem}
          onRemoveCartItem={handleRemoveCartItem}
          onToggleWishlist={handleToggleWishlist}
        />
      );
    }

    if (route.page === "product") {
      return (
        <ProductDetails
          product={selectedProduct}
          onAddToCart={handleAddToCart}
          onToggleWishlist={handleToggleWishlist}
          isWishlisted={Boolean(wishlistItems[selectedProduct.id])}
        />
      );
    }

    if (route.page === "about") {
      return <About />;
    }

    if (route.page === "contact") {
      return <Contact />;
    }

    if (route.page === "blog") {
      return (
        <Blog
          onOpenDetails={handleOpenProduct}
          onAddToCart={handleAddToCart}
          onToggleWishlist={handleToggleWishlist}
          cartItems={cartItems}
          wishlistItems={wishlistItems}
        />
      );
    }

    return <Home />;
  };

  return (
    <>
      <Navbar
        cartCount={cartCount}
        cartProducts={cartProducts}
        cartTotal={cartTotal}
        products={products}
        wishlistProducts={wishlistProducts}
        onAddToCart={handleAddToCart}
        onDecreaseCartItem={handleDecreaseCartItem}
        onRemoveCartItem={handleRemoveCartItem}
        onOpenDetails={handleOpenProduct}
        openLogin={openLogin}
        openAccount={openAccount}
        currentUser={currentUser}
        onLogout={handleLogout}
      />
      {showLogin && (
        <Login
          closeLogin={closeLogin}
          initialMode={loginMode}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      {showAccount && currentUser && (
        <UserDashboard
          user={currentUser}
          cartCount={cartCount}
          wishlistCount={wishlistProducts.length}
          onClose={closeAccount}
          onLogout={handleLogout}
        />
      )}
      {renderPage()}
    </>
  );
}

export default App;
